// Shared theme vocabulary for themed-surface components (RCThemedBox, RCCallout).
// This is the TypeScript counterpart to the `themed-surface` SCSS mixin: the
// types describe the valid theme/variant combinations, and `themeModifier`
// builds the matching BEM modifier class for a given block.

export type UtilityColor = 'info' | 'loud' | 'neutral' | 'notice' | 'success' | 'urgent';
type UtilityFill = 'ultralight' | 'medium' | 'bold';

/**
 * A utility theme variant. The bare family (e.g. `info`) is the transparent,
 * accent-only form (no fill — pair with a `border`); a fill suffix adds a
 * background tint (`info-ultralight` / `info-medium` / `info-bold`).
 */
export type UtilityVariant = UtilityColor | `${UtilityColor}-${UtilityFill}`;

export type SurfaceVariant = 'primary' | 'on-primary';

/** A curated, brand-approved tempo combination. `{background}[-{accent-text-family}]`. */
export type TempoTheme =
  // High
  | 'vu-high'
  | 'tweed-high' | 'tweed-high-vu'
  | 'teal-high' | 'teal-high-kick'
  | 'kick-high' | 'kick-high-teal'
  | 'yellow-high' | 'yellow-high-vu'
  // Mid
  | 'vu-mid' | 'vu-mid-vu' | 'vu-mid-dark'
  | 'tweed-mid' | 'tweed-mid-vu'
  | 'teal-mid' | 'teal-mid-kick' | 'teal-mid-teal'
  | 'teal-mid-dark'
  | 'kick-mid-dark'
  // Neutral / low
  | 'neutral-vu' | 'neutral-kick' | 'neutral-teal';

export type ThemeType = 'utility' | 'surface' | 'tempo';

/**
 * The theme is a top-level discriminated union: `themeType` selects the scheme
 * and narrows the valid `variant`. Omit both for a plain, unthemed element.
 */
export type ThemeProps =
  | { themeType?: undefined; variant?: undefined }
  | { themeType: 'utility'; variant: UtilityVariant }
  | { themeType: 'surface'; variant?: SurfaceVariant }
  | { themeType: 'tempo'; variant: TempoTheme };

/**
 * Builds the theme modifier class for a block, e.g.
 * `themeModifier('rc-callout', 'utility', 'info-bold')` -> `rc-callout--utility-info-bold`.
 * `surface` defaults its variant to `primary`; `utility`/`tempo` require one.
 */
export function themeModifier(
  block: string,
  themeType?: ThemeType,
  variant?: UtilityVariant | SurfaceVariant | TempoTheme,
): string | undefined {
  if (!themeType) return undefined;

  const resolved = themeType === 'surface' ? variant ?? 'primary' : variant;
  return resolved ? `${block}--${themeType}-${resolved}` : undefined;
}
